// ThemeProvider — runtime brand identity on the document per DESIGN-TOKENS
// §Color + §Dark mode.
//
// Responsibilities:
//   (1) Apply `.dark` class to <html> based on me.user.theme_mode (light /
//       dark / system). theme-init.js handled this BEFORE first paint
//       reading localStorage; this Provider takes over after React mounts,
//       respecting the user's saved preference from /me.
//   (2) On project-scoped routes, override Layer 1 brand vars
//       (--color-primary, --color-on-primary, derived alpha variants, and
//       --color-primary-display via OKLCH JS fallback when @supports false)
//       on <html> style. Off project routes: clear inline styles so :root
//       defaults (platform brand) take over.
//   (3) Brand the browser chrome: re-tint the favicon to the active project
//       (platform default off-project) + set a meaningful tab title.
//
// Pre-auth (no me data) — does nothing; :root defaults already showing,
// theme-init.js handled the .dark class. Authed-project-less — applies
// theme_mode only; brand stays at platform default. Authed-project-scoped —
// full pass (mode + brand). The three frontend traversal patterns per
// ANATOMY §Axis 3 are respected.

import { useEffect, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { useMe } from '../../hooks/useMe.js';
import { useProject } from '../../hooks/useProject.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { clampLightness, deriveOnDisplay } from '../../lib/oklch-fallback.js';
import { cubeFaviconHref, setFavicon } from '../../lib/favicon.js';
import { PLATFORM_NAME } from '../../branding.js';
import { useTitleStore } from '../../stores/titleStore.js';

// Test once at module load (cheap; result is constant for the page lifetime).
const supportsOklchRelative = (() => {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('color', 'oklch(from white l c h)');
})();

function hexToAlphaRgb(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function applyBrand(primary: string, onPrimary: string, isDark: boolean): void {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', primary);
  root.style.setProperty('--color-on-primary', onPrimary);

  // Derived tokens follow the DISPLAY (contrast-clamped) value, not raw brand
  // (DESIGN-TOKENS §Color): in dark mode a dark brand's 10% raw wash is
  // invisible on near-black, so the hover/selected wash derives from the
  // clamped color at a higher alpha. The focus ring is NOT set here — it
  // follows --color-primary-display through the CSS chain (solid ring +
  // light-mode lightness ceiling live in index.css).
  const displayHex = isDark ? clampLightness(primary, 0.65) : primary;
  root.style.setProperty('--color-primary-light', hexToAlphaRgb(displayHex, isDark ? 0.16 : 0.1));
  // Ink on the display fill: the stored ink while it clears AA on the fill as
  // rendered; flips (near-black on lifted fills) when the dark clamp takes the
  // fill out of reach — DESIGN-TOKENS §Color Layer 1.
  root.style.setProperty('--color-on-primary-display', deriveOnDisplay(displayHex, onPrimary));

  // OKLCH dark-mode display: CSS handles it when @supports passes.
  // When @supports fails AND we're in dark mode, JS fallback sets the clamped
  // value inline. In light mode --color-primary-display always equals
  // --color-primary, so unset to let :root's `var(--color-primary)` take over.
  if (isDark && !supportsOklchRelative) {
    root.style.setProperty('--color-primary-display', displayHex);
  } else {
    root.style.removeProperty('--color-primary-display');
  }
}

function clearBrand(): void {
  const root = document.documentElement;
  root.style.removeProperty('--color-primary');
  root.style.removeProperty('--color-on-primary');
  root.style.removeProperty('--color-primary-light');
  root.style.removeProperty('--color-primary-ring');
  root.style.removeProperty('--color-primary-display');
  root.style.removeProperty('--color-on-primary-display');
}

function resolveDark(mode: 'light' | 'dark' | 'system' | undefined): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  // 'system' or undefined → match OS
  if (typeof matchMedia === 'undefined') return false;
  return matchMedia('(prefers-color-scheme: dark)').matches;
}

export interface ThemeProviderProps {
  children: ReactNode;
}

const selectThemeMode = (me: { user: { theme_mode: 'light' | 'dark' | 'system' } }) =>
  me.user.theme_mode;
const selectAvailableProjects = (me: MeResponse) => me.availableProjects;

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { data: themeMode } = useMe(selectThemeMode);
  const projectId = useOptionalProjectParams();
  const { data: project } = useProject(projectId ?? '');
  const { data: availableProjects } = useMe(selectAvailableProjects);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // The project whose brand the document wears. Project-scoped routes use the
  // path param's full project (members + sysadmin cross-project). The
  // /project-blocked terminal page brands from its `?project=` — a membership
  // the access gate refused (GET /projects/:id 403s on suspended/expired), so
  // the colour comes from /me's availableProjects, which the user legitimately
  // holds. Branding only — access stays the path param + backend gate (ADR-016).
  const blockedId =
    projectId === null && location.pathname === '/project-blocked'
      ? searchParams.get('project')
      : null;
  const blockedProject = blockedId
    ? availableProjects?.find((p) => p.id === blockedId)
    : undefined;

  const brandPrimary = project?.color_primary ?? blockedProject?.color_primary;
  const brandOnPrimary = project?.color_on_primary ?? blockedProject?.color_on_primary;
  const brandName = project?.display_name || project?.name || blockedProject?.name;
  // True platform context (no path project, no branded blocked project) — the
  // only state that clears per-project brand / restores the default favicon.
  const offProject = projectId === null && !blockedProject;

  // (1) Apply .dark class based on user's theme_mode (or system).
  useEffect(() => {
    const isDark = resolveDark(themeMode);
    document.documentElement.classList.toggle('dark', isDark);
    // Persist for theme-init.js next page-load (so pre-React reload doesn't
    // flash the wrong mode for users whose pref differs from system).
    if (themeMode) {
      try {
        localStorage.setItem('theme_mode', themeMode);
      } catch {
        // localStorage may be disabled; non-fatal.
      }
    }
  }, [themeMode]);

  // (2) Apply Layer 1 brand for the resolved brand project (path-param project
  //     OR the blocked page's project); clear to platform default only in true
  //     platform context.
  useEffect(() => {
    const isDark = resolveDark(themeMode);
    if (brandPrimary && brandOnPrimary) {
      applyBrand(brandPrimary, brandOnPrimary, isDark);
    } else if (offProject) {
      // Platform context — clear any previously-applied per-project brand.
      clearBrand();
    }
    // When a project id is set but its data hasn't arrived yet, leave previous
    // values in place (avoid flash) — refresh once data loads.
  }, [brandPrimary, brandOnPrimary, offProject, themeMode]);

  // (3) Favicon — tinted to the active project (platform default off-project).
  //     While a project is loading, leave the previous favicon (avoid a flicker).
  useEffect(() => {
    if (brandPrimary && brandOnPrimary) {
      setFavicon(cubeFaviconHref(brandPrimary, brandOnPrimary));
    } else if (offProject) {
      setFavicon(cubeFaviconHref());
    }
  }, [brandPrimary, brandOnPrimary, offProject]);

  // (3b) Tab title — `[page leaf · context]`, most-specific-first; the brand fills
  //      the context slot ONLY when there is no project to name (e.g. platform /
  //      pre-auth) — FRONTEND-PATTERNS §Document title. The leaf is pushed up by
  //      the mounted page via useDocumentTitle; context = the active project's
  //      name, else RubixEMS.
  const titleSegment = useTitleStore((s) => s.segment);
  useEffect(() => {
    const context = brandName ?? PLATFORM_NAME;
    document.title = [titleSegment, context].filter(Boolean).join(' · ');
  }, [titleSegment, brandName]);

  // (4) Re-evaluate system preference if user is on 'system' mode and OS flips.
  //     Brand derivations are mode-aware (the hover-wash alpha + the JS display
  //     fallback), so re-apply them with the new mode.
  useEffect(() => {
    if (themeMode !== 'system' && themeMode !== undefined) return;
    if (typeof matchMedia === 'undefined') return;
    const mql = matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      document.documentElement.classList.toggle('dark', mql.matches);
      if (brandPrimary && brandOnPrimary) {
        applyBrand(brandPrimary, brandOnPrimary, mql.matches);
      }
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [themeMode, brandPrimary, brandOnPrimary]);

  return <>{children}</>;
}
